import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { Control, FieldValues, UseFormReturn } from 'react-hook-form';
import {
  Alert,
  AlertDescription,
  Badge,
  Button,
  Flex,
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Icon,
  Input,
  Label,
  RadioGroup,
  RadioGroupItem,
  Text,
} from '@kognitos/lattice';

import type {
  Bdkv2BookAuthenticationDescriptor,
  Bdkv2BookDescriptor,
  Bdkv2CredentialDescriptor,
} from '@/generated/hooks/bdk/models';
import {
  AdvancedSection,
  CredentialPicker,
  DeleteCredentialDialog,
  ECredentialScope,
  useCredentialPermissions,
  useRenameCredential,
  type ICredentialOptionActions,
  type ICredentialCreateFormValues,
  type TCredential,
  type TCredentialSelection,
} from '@/modules/credentials';
import {
  selectCurrentOrganizationId,
  selectCurrentWorkspaceId,
  useUserPreferencesStore,
} from '@/shared/stores/user-preferences.store';
import { getNoAutofillProps } from '@/shared/utils';
import { convertToDataUrl } from '@/shared/utils/image-utils';

import { CustomCredentialsStep } from './CustomCredentialsStep';
import { EditCredentialValuesDialog } from './EditCredentialValuesDialog';
import { OAuthSignInButton } from './OAuthSignInButton';

/** Props for {@link ManagedCredentialStep}. */
export interface IManagedCredentialStepProps {
  integration: Bdkv2BookDescriptor;
  authMethodId: string;
  /** Unique per environment, e.g. `draft` / `published`. */
  idPrefix: string;
  credentials: TCredential[];
  isLoading: boolean;
  /** The compatible credentials couldn't be loaded. */
  isError: boolean;
  selection: TCredentialSelection | null;
  /** `null` returns to the list with nothing picked. */
  onSelectionChange: (selection: TCredentialSelection | null) => void;
  form: UseFormReturn<ICredentialCreateFormValues>;
  /** Name a new credential gets unless renamed. */
  defaultDisplayName: string;
  /** Whether the user may create organization-wide credentials. */
  canChooseScope: boolean;
  /**
   * OAuth: the app's arguments to fill in when creating, instead of the auth
   * method's credential fields.
   */
  oauthArguments?: Bdkv2CredentialDescriptor[];
  /** OAuth: the provider's name, e.g. `Google`. */
  oauthProviderName?: string;
  /**
   * OAuth: signs in with another account, shown as the provider's sign-in
   * button in place of "Create a new credential".
   */
  oauthSignIn?: {
    authentication: Bdkv2BookAuthenticationDescriptor;
    onClick: () => void;
    isLoading: boolean;
  };
  /** A credential managed by Kognitos is used as-is; there is nothing to pick. */
  managedCredentialName?: string;
  /** OAuth: picks the app a new credential signs in through. */
  appPicker?: ReactNode;
  /** OAuth: the sign-in button shown under the create form's fields. */
  inlineSignIn?: ReactNode;
  /** OAuth behind a shared app: sign in through the organization's own app instead. */
  ownAppOption?: () => void;
}

/**
 * The connect wizard's credential step for integrations that support remote
 * secrets. Either lists the compatible credentials to pick from, or — when
 * creating — replaces the list with the new credential's form, whose fields are
 * the auth method's own credential fields (same labels, descriptions and
 * masking as the classic flow).
 *
 * @param props - Component props.
 * @returns The managed credential step.
 */
export function ManagedCredentialStep({
  integration,
  authMethodId,
  idPrefix,
  credentials,
  isLoading,
  isError,
  selection,
  onSelectionChange,
  form,
  defaultDisplayName,
  canChooseScope,
  oauthArguments,
  oauthProviderName,
  oauthSignIn,
  managedCredentialName,
  appPicker,
  inlineSignIn,
  ownAppOption,
}: IManagedCredentialStepProps) {
  const { t } = useTranslation('credentials');
  const organizationId = useUserPreferencesStore(selectCurrentOrganizationId);
  const workspaceId = useUserPreferencesStore(selectCurrentWorkspaceId);
  const [credentialToDelete, setCredentialToDelete] =
    useState<TCredential | null>(null);

  // Credentials managed by Kognitos can't be deleted.
  const permissions = useCredentialPermissions(organizationId, workspaceId);

  const [credentialToEdit, setCredentialToEdit] = useState<TCredential | null>(
    null,
  );
  const { rename } = useRenameCredential(organizationId, workspaceId);
  // A key/value auth method's credentials have values to edit; OAuth ones
  // sign in again through Reconnect instead.
  const valuesAuthentication = integration.authentications?.find(
    auth =>
      auth.custom?.id === authMethodId &&
      (auth.custom.credentials?.length ?? 0) > 0,
  );

  // Credentials managed by Kognitos are read-only.
  const credentialActions = (
    credential: TCredential,
  ): ICredentialOptionActions => ({
    rename: permissions.canRename(credential)
      ? displayName => rename(credential.id ?? '', displayName)
      : undefined,
    editValues:
      valuesAuthentication && permissions.canEditValues(credential)
        ? () => setCredentialToEdit(credential)
        : undefined,
    delete: permissions.canDelete(credential)
      ? () => setCredentialToDelete(credential)
      : undefined,
  });
  const ownAppLink = ownAppOption && (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      className="self-center text-muted-foreground"
      onClick={ownAppOption}
    >
      {t('credentials.oauth.useOwnApp')}
    </Button>
  );

  const loadErrorAlert = isError && (
    <Alert>
      <Icon type="AlertTriangle" size="sm" />
      <AlertDescription>{t('credentials.picker.loadError')}</AlertDescription>
    </Alert>
  );

  if (managedCredentialName) {
    return (
      <Flex direction="column" gap={1} width="full">
        <Text weight="medium" level="small">
          {t('credentials.picker.label')}
        </Text>
        <Flex align="center" gap={2}>
          <Text>{managedCredentialName}</Text>
          <Badge variant="outline">{t('credentials.managedByKognitos')}</Badge>
        </Flex>
      </Flex>
    );
  }

  const isOAuth = !!oauthArguments;
  // The book's own OAuth method: new accounts sign in through an app the user picks.
  const usesOwnApp = !!appPicker;

  if (selection?.mode !== 'create') {
    return (
      <Flex direction="column" align="stretch" gap={3} width="full">
        {loadErrorAlert}
        <CredentialPicker
          idPrefix={idPrefix}
          credentials={credentials}
          isLoading={isLoading}
          description={
            usesOwnApp
              ? t('credentials.oauth.ownAppDescription')
              : isOAuth
                ? t('credentials.oauth.existingDescription', {
                    provider: oauthProviderName,
                  })
                : undefined
          }
          createLabel={
            usesOwnApp ? t('credentials.oauth.authenticateNew') : undefined
          }
          createAction={
            oauthSignIn && (
              <OAuthSignInButton
                integration={integration}
                authentication={oauthSignIn.authentication}
                disabled={false}
                isLoading={oauthSignIn.isLoading}
                onClick={oauthSignIn.onClick}
              />
            )
          }
          selectedCredentialId={
            selection?.mode === 'existing' ? selection.credentialId : undefined
          }
          onSelect={credentialId =>
            onSelectionChange({ mode: 'existing', credentialId })
          }
          onCreate={() => onSelectionChange({ mode: 'create' })}
          actionsFor={credentialActions}
        />
        {ownAppLink}
        <DeleteCredentialDialog
          organizationId={organizationId}
          workspaceId={workspaceId}
          credential={credentialToDelete}
          integrationDisplayName={integration.display_name || integration.name}
          integrationIconSrc={convertToDataUrl(integration.icon)}
          onClose={() => setCredentialToDelete(null)}
          onDeleted={credentialId => {
            if (
              selection?.mode === 'existing' &&
              selection.credentialId === credentialId
            ) {
              onSelectionChange(null);
            }
          }}
        />
        {valuesAuthentication && (
          <EditCredentialValuesDialog
            organizationId={organizationId}
            workspaceId={workspaceId}
            integration={integration}
            authentication={valuesAuthentication}
            credential={credentialToEdit}
            onClose={() => setCredentialToEdit(null)}
          />
        )}
      </Flex>
    );
  }

  return (
    <Form {...form}>
      <Flex direction="column" align="stretch" gap={4} width="full">
        {loadErrorAlert}

        <Text weight="medium">{t('credentials.create.title')}</Text>
        <CredentialNameField
          form={form}
          defaultDisplayName={defaultDisplayName}
        />

        {appPicker}

        {oauthArguments ? (
          oauthArguments.map(argument => (
            <FormField
              key={argument.id}
              control={form.control}
              name={`values.${argument.id ?? ''}`}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{argument.label || argument.id}</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      value={field.value ?? ''}
                      {...getNoAutofillProps()}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          ))
        ) : (
          <CustomCredentialsStep
            integration={integration}
            authMethodId={authMethodId}
            control={form.control as unknown as Control<FieldValues>}
            fieldPrefix="values"
          />
        )}

        {inlineSignIn}
        {ownAppLink}

        {canChooseScope && !isOAuth && (
          <AdvancedSection>
            <FormField
              control={form.control}
              name="scope"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('credentials.create.scope.label')}</FormLabel>
                  <FormControl>
                    <RadioGroup
                      value={field.value}
                      onValueChange={field.onChange}
                      className="flex flex-col gap-2"
                    >
                      <Flex align="center" gap={2}>
                        <RadioGroupItem
                          value={ECredentialScope.Workspace}
                          id={`${idPrefix}-scope-workspace`}
                        />
                        <Label htmlFor={`${idPrefix}-scope-workspace`}>
                          {t('credentials.create.scope.workspace')}
                        </Label>
                      </Flex>
                      <Flex align="center" gap={2}>
                        <RadioGroupItem
                          value={ECredentialScope.Organization}
                          id={`${idPrefix}-scope-organization`}
                        />
                        <Label htmlFor={`${idPrefix}-scope-organization`}>
                          {t('credentials.create.scope.organization')}
                        </Label>
                      </Flex>
                    </RadioGroup>
                  </FormControl>
                  <FormDescription>
                    {t('credentials.create.scope.description')}
                  </FormDescription>
                </FormItem>
              )}
            />
          </AdvancedSection>
        )}
      </Flex>
    </Form>
  );
}

/** Props for {@link CredentialNameField}. */
interface ICredentialNameFieldProps {
  form: UseFormReturn<ICredentialCreateFormValues>;
  /** Shown, and saved, until the user types a name of their own. */
  defaultDisplayName: string;
}

/**
 * The new credential's name: an input showing the suggested name until the
 * user changes it. A blank name falls back to the suggestion on save.
 *
 * @param props - Component props.
 * @returns The name field.
 */
function CredentialNameField({
  form,
  defaultDisplayName,
}: ICredentialNameFieldProps) {
  const { t } = useTranslation('credentials');
  // The suggestion follows the connection name until the user types their own.
  const [isEdited, setIsEdited] = useState(false);
  return (
    <FormField
      control={form.control}
      name="displayName"
      render={({ field }) => (
        <FormItem>
          <FormLabel>{t('credentials.create.displayName.label')}</FormLabel>
          <FormControl>
            <Input
              {...field}
              {...getNoAutofillProps()}
              value={isEdited || field.value ? field.value : defaultDisplayName}
              placeholder={defaultDisplayName}
              onChange={event => {
                setIsEdited(true);
                field.onChange(event.target.value);
              }}
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
}
